import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Card,
    CardContent,
    CardHeader,
    InputAdornment,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    FormControlLabel,
    Checkbox,
    ListItemIcon,
    TableFooter
} from '@mui/material';
import {
    Search,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    X,
    MeetingRoom,
    Person,
    Phone,
    Email,
    CreditCard,
    CheckCircle,
    CalendarToday,
    Receipt,
    VerifiedUser,
    AccessTime,
    Business,
    People,
    Group
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    APIBookingList,
    APIBookingCheckIn,
    APIRoomNames,
    APICustomerNames
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
    formatCreatedAtDate,
    formatNumberWithComma,
    getCategoriesNameById,
    getStatusById,
    getUserDetails,
    recordsPerPage,
    formatCurrency,
    formatNumberWithComma2
} from '../../utils/helpers';
import {
    exportToCSV,
    exportToExcel,
    exportToPDF
} from '../../services/reservation/CheckInService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';

const CheckIn = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [earlyCheckInModal, setEarlyCheckInModal] = useState(false);

    // State management
    const [pageState, setPageState] = useState({
        pageData: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Data states
    const [rooms, setRooms] = useState([]);

    // Filters state
    const [filters, setFilters] = useState({
        booking_no: '',
        customer_name: '',
        room_type: '',
        status: '1', // Default to active bookings for check-in
        dateFrom: '',
        dateTo: ''
    });

    // Check-in form state
    const [checkInForm, setCheckInForm] = useState({
        booking_id: '',
        comments: '',
        branch_id: getUserDetails("branchId"),
        performed_by: getUserDetails("name") || ''
    });

    // Dialog states
    const [filterOptions, setFilterOptions] = useState(false);
    const [openCheckIn, setOpenCheckIn] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    // Stepper state for check-in process
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Booking Details', 'Customer Verification', 'Confirm Check-in'];

    // Permissions
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        setUserRole(getUserRole());
        fetchData();
        fetchRooms();
    }, []);

    // Fetch data functions
    const fetchRooms = async () => {
        try {
            const response = await axios.get(APIRoomNames);
            if (response.data.status === 'success') {
                setRooms(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching room names:", error);
            showToast("Error", "Failed to load rooms", "error");
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = { status: 1 }; // Only fetch active bookings for check-in
            const bookingRes = await axios.get(APIBookingList, { params });

            const bookings = Array.isArray(bookingRes.data.data) ? bookingRes.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                pageData: bookings
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    // Permission check
    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    // Helper function to get customer/organisation display name - WITH NULL CHECK
    const getCustomerDisplayName = (record) => {
        if (!record) return 'N/A';
        if (record.booking_type === 2) {
            return record.organisation_name || 'N/A';
        }
        return record.customer_name || 'N/A';
    };

    // Helper function to get customer mobile - WITH NULL CHECK
    const getCustomerMobile = (record) => {
        if (!record) return 'N/A';
        if (record.booking_type === 2) {
            // For organisation, show first member's phone or organisation phone if available
            if (record.organisation_members && record.organisation_members.length > 0) {
                return record.organisation_members[0].phone || 'N/A';
            }
            return 'N/A';
        }
        return record.customer_mobile || 'N/A';
    };

    // Helper function to get total guests count - WITH NULL CHECK
    const getTotalGuests = (record) => {
        if (!record) return 0;
        if (record.booking_type === 2) {
            return record.organisation_members?.length || 0;
        }
        return (record.adults || 1) + (record.children || 0);
    };

    // Helper function to get booking type label
    const getBookingTypeLabel = (record) => {
        if (!record) return 'N/A';
        return record.booking_type === 1 ? 'Individual' : 'Organisation';
    };

    // Filter data
    const filteredData = pageState.pageData.filter(record => {
        const customerDisplay = getCustomerDisplayName(record);

        const matchesSearch = searchTerm === '' ||
            (record.booking_no && record.booking_no.toString().includes(searchTerm)) ||
            (customerDisplay.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesBookingNo = !filters.booking_no ||
            (record.booking_no && record.booking_no.toString().includes(filters.booking_no));

        const matchesCustomer = !filters.customer_name ||
            (customerDisplay.toLowerCase().includes(filters.customer_name.toLowerCase()));

        const matchesRoom = !filters.room_type ||
            (record.room_type && record.room_type.toString() === filters.room_type.toString());

        const matchesStatus = !filters.status ||
            (record.status && record.status.toString().includes(filters.status));

        // Date range filter
        let matchesDate = true;
        if (filters.dateFrom && record.check_in) {
            const recordDate = new Date(record.check_in).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo && record.check_out) {
            const recordDate = new Date(record.check_out).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesBookingNo && matchesCustomer && matchesRoom && matchesStatus && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleOpenCheckIn = (record) => {
        setSelectedRecord(record);
        setCheckInForm({
            booking_id: record.id,
            comments: '',
            branch_id: getUserDetails("branchId"),
            performed_by: getUserDetails("name") || ''
        });
        setOpenCheckIn(true);
    };

    const handleCloseCheckIn = () => {
        setOpenCheckIn(false);
        setActiveStep(0);
        setSelectedRecord(null);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedRecord(null);
    };

    // Form handlers
    const handleCheckInFormChange = (e) => {
        const { name, value } = e.target;
        setCheckInForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            booking_no: '',
            customer_name: '',
            room_type: '',
            status: '1',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setFilterOptions(false);
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            const checkedInData = pageState.pageData;
            await exportToExcel(checkedInData, { roomsData: rooms }, filters);
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            const checkedInData = pageState.pageData;
            await exportToPDF(checkedInData, rooms, filters);
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            const checkedInData = pageState.pageData;
            await exportToCSV(checkedInData, { roomsData: rooms });
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    // Stepper handlers
    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    // Action handlers
    const handleCheckIn = async () => {
        try {
            setLoading(true);

            // Check if booking can be checked in
            const checkInDate = new Date(selectedRecord.check_in);
            const now = new Date();

            // Allow check-in if:
            // 1. Status is active (1) AND
            // 2. Current time is on or after check-in time
            if (selectedRecord.status !== 1) {
                showToast("Error", "Only active bookings can be checked in", "error");
                setLoading(false);
                return;
            }

            // Check if it's early check-in
            const isEarlyCheckIn = now < checkInDate;

            if (isEarlyCheckIn) {
                // Show modal for early check-in confirmation
                setEarlyCheckInModal(true);
                setLoading(false);
                return; // Wait for user confirmation
            }

            // If not early check-in, proceed directly
            await performCheckIn();

        } catch (error) {
            console.error('Check-in error:', error);
            setLoading(false);
            if (error.response) {
                showToast("Error", error.response.data.message || "Check-in failed", "error");
            } else {
                showToast("Error", "Network error. Please try again.", "error");
            }
        }
    };

    // Separate function to perform the actual check-in
    const performCheckIn = async () => {
        try {
            console.log("checkInForm"+JSON.stringify(checkInForm))
            const response = await axios.post(APIBookingCheckIn, checkInForm);

            if (response.data.status === 'success') {
                showToast("Success", "Guest checked in successfully", "success");
                handleCloseCheckIn();
                fetchData();
            } else {
                showToast("Error", response.data.message || "Check-in failed", "error");
            }
        } catch (error) {
            console.error('Check-in API error:', error);
            if (error.response) {
                showToast("Error", error.response.data.message || "Check-in failed", "error");
            }
        } finally {
            setLoading(false);
            setEarlyCheckInModal(false);
        }
    };

    // Handle early check-in confirmation
    const handleEarlyCheckInConfirm = () => {
        performCheckIn();
    };

    const handleEarlyCheckInCancel = () => {
        setEarlyCheckInModal(false);
        setLoading(false);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 }
            }}>
                Check-in Management ({formatNumberWithComma2(pageState.pageData.length || 0)})
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                }}>
                    {/* Buttons */}
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexShrink: 0,
                        order: { xs: 2, sm: 1 }
                    }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={() => setFilterOptions(true)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                            size="small"
                        >
                            View Options
                        </Button>

                        {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Search Bar */}
                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        order: { xs: 1, sm: 2 },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search Bookings..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.booking_no || filters.dateFrom || filters.dateTo || filters.status || filters.room_type || filters.customer_name) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.booking_no && (
                            <Chip
                                label={`Booking #: ${filters.booking_no}`}
                                onDelete={() => setFilters(prev => ({ ...prev, booking_no: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.customer_name && (
                            <Chip
                                label={`Customer: ${filters.customer_name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, customer_name: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.room_type && (
                            <Chip
                                label={`Room: ${getCategoriesNameById(filters.room_type, rooms)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, room_type: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusById(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ minWidth: 'auto' }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Booking #</TableCell>
                                    <TableCell>Customer/Organisation</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Guests</TableCell>
                                    <TableCell>Check In</TableCell>
                                    <TableCell>Check Out</TableCell>
                                    <TableCell>Room Type</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((booking, index) => (
                                    <TableRow
                                        key={booking.id}
                                        hover
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: theme.palette.action.hover
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            {booking.booking_no || `BK${booking.id.toString().padStart(5, '0')}`}
                                        </TableCell>
                                        <TableCell>
                                            {booking.booking_type === 2 ? (
                                                <>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {booking.organisation_name || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        <People fontSize="small" sx={{ fontSize: 12, mr: 0.5 }} />
                                                        {booking.organisation_members?.length || 0} members
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="body2">
                                                        {booking.customer_name || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {booking.customer_mobile || ''}
                                                    </Typography>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.booking_type === 1 ? 'Individual' : 'Organisation'}
                                                size="small"
                                                color={booking.booking_type === 1 ? 'default' : 'secondary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getTotalGuests(booking)}
                                                size="small"
                                                color="info"
                                                icon={<Group />}
                                            />
                                        </TableCell>
                                        <TableCell>{booking.check_in ? formatCreatedAtDate(booking.check_in) : 'N/A'}</TableCell>
                                        <TableCell>{booking.check_out ? formatCreatedAtDate(booking.check_out) : 'N/A'}</TableCell>
                                        <TableCell>
                                            {getCategoriesNameById(booking.room_type, rooms)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatCurrency(booking.total_amount || 0)}
                                                color="success"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {booking.status === 1 ? (
                                                <Chip
                                                    label={getStatusById(booking.status)}
                                                    size="small"
                                                    color="warning"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : booking.status === 2 ? (
                                                <Chip
                                                    label="Checked In"
                                                    size="small"
                                                    color="success"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : booking.status === 3 ? (
                                                <Chip
                                                    label="Checked Out"
                                                    size="small"
                                                    color="info"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : (
                                                <Chip
                                                    label={getStatusById(booking.status)}
                                                    size="small"
                                                    color="error"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleOpenView(booking)}
                                                color="primary"
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.info.light
                                                    }
                                                }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            {booking.status === 1 && (
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenCheckIn(booking)}
                                                    color="success"
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.success.light
                                                        }
                                                    }}
                                                    title="Check In"
                                                >
                                                    <CheckCircle />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No bookings found</Typography>
                            <Typography variant="body2">Try adjusting your search query</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>

            {/* Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={() => setFilterOptions(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Bookings
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Bookings
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Booking Number
                        </Typography>

                        <TextField
                            fullWidth
                            label="Booking Number"
                            name="booking_no"
                            value={filters.booking_no}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Customer/Organisation Name
                        </Typography>

                        <TextField
                            fullWidth
                            label="Customer/Organisation Name"
                            name="customer_name"
                            value={filters.customer_name}
                            onChange={handleFilterChange}
                        />

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Room Type
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Room Type</InputLabel>
                            <Select
                                name="room_type"
                                value={filters.room_type}
                                onChange={handleFilterChange}
                                label="Room Type"
                            >
                                <MenuItem value="">All Rooms</MenuItem>
                                {rooms.map((room) => (
                                    <MenuItem key={room.id} value={room.id}>
                                        {room.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Status
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="1">Active</MenuItem>
                                <MenuItem value="2">Checked In</MenuItem>
                                <MenuItem value="3">Checked Out</MenuItem>
                                <MenuItem value="0">Cancelled</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setFilterOptions(false)}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => setFilterOptions(false)}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Check-in Dialog */}
            <Dialog
                open={openCheckIn}
                onClose={handleCloseCheckIn}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: 1
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
                        Check In {selectedRecord?.booking_type === 2 ? 'Organisation' : 'Guest'}
                    </Typography>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseCheckIn}
                        aria-label="close"
                        size="small"
                    >
                        <X />
                    </IconButton>
                </DialogTitle>

                {/* Stepper */}
                <Box sx={{ px: 3, pt: 2 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <DialogContent dividers>
                    {activeStep === 0 && (
                        <Card>
                            <CardHeader
                                title="Booking Information"
                                avatar={<Receipt color="primary" />}
                            />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Booking Number:</Typography>
                                        <Typography variant="h6" color="primary">
                                            {selectedRecord?.booking_no || `BK${selectedRecord?.id?.toString().padStart(5, '0')}`}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Booking Type:</Typography>
                                        <Chip
                                            label={getBookingTypeLabel(selectedRecord)}
                                            size="small"
                                            color={selectedRecord?.booking_type === 1 ? 'default' : 'secondary'}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {selectedRecord?.booking_type === 2 ? 'Organisation Name:' : 'Customer Name:'}
                                        </Typography>
                                        <Typography variant="h6">
                                            {getCustomerDisplayName(selectedRecord)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Contact:</Typography>
                                        <Typography variant="body1">
                                            {getCustomerMobile(selectedRecord)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Check In:</Typography>
                                        <Typography variant="body1">
                                            {formatCreatedAtDate(selectedRecord?.check_in)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Check Out:</Typography>
                                        <Typography variant="body1">
                                            {formatCreatedAtDate(selectedRecord?.check_out)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Room:</Typography>
                                        <Typography variant="body1">
                                            {getCategoriesNameById(selectedRecord?.room_type, rooms)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Total Guests:</Typography>
                                        <Chip
                                            label={getTotalGuests(selectedRecord)}
                                            size="small"
                                            color="info"
                                            icon={<Group />}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Total Amount:</Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {formatCurrency(selectedRecord?.total_amount || 0)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Advance Paid:</Typography>
                                        <Typography variant="body1" color="success">
                                            {formatCurrency(selectedRecord?.advance_amount || 0)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Balance Due:</Typography>
                                        <Typography variant="body1" color="warning">
                                            {formatCurrency((selectedRecord?.total_amount || 0) - (selectedRecord?.advance_amount || 0))}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {selectedRecord?.booking_type === 2 && selectedRecord?.organisation_members && selectedRecord.organisation_members.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Organisation Members ({selectedRecord.organisation_members.length}):
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Phone</TableCell>
                                                        <TableCell>Email</TableCell>
                                                        <TableCell>Room</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedRecord.organisation_members.map((member, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{member.name}</TableCell>
                                                            <TableCell>{member.phone}</TableCell>
                                                            <TableCell>{member.email || '-'}</TableCell>
                                                            <TableCell>{getCategoriesNameById(member.room_type, rooms)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeStep === 1 && (
                        <Card>
                            <CardHeader
                                title="Customer Verification"
                                avatar={<VerifiedUser color="primary" />}
                            />
                            <CardContent>
                                <List>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar>
                                                <Person />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={selectedRecord?.booking_type === 2 ? "Organisation Representative Identity" : "Customer Identity"}
                                            secondary={`Verify ${selectedRecord?.booking_type === 2 ? 'representative ID' : 'customer ID card or passport'}`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar>
                                                <Group />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={selectedRecord?.booking_type === 2 ? "All Members Present" : "Guest Count Verification"}
                                            secondary={selectedRecord?.booking_type === 2 
                                                ? `Confirm all ${selectedRecord?.organisation_members?.length || 0} members are present`
                                                : `Verify ${getTotalGuests(selectedRecord)} guests are present`}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar>
                                                <CreditCard />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary="Payment Verification"
                                            secondary="Confirm advance payment details"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar>
                                                <MeetingRoom />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary="Room Inspection"
                                            secondary="Room is clean and ready for occupancy"
                                        />
                                    </ListItem>
                                </List>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Please verify all details and ensure room is prepared before check-in.
                                </Alert>
                            </CardContent>
                        </Card>
                    )}

                    {activeStep === 2 && (
                        <Card>
                            <CardHeader
                                title="Final Confirmation"
                                avatar={<CheckCircle color="success" />}
                            />
                            <CardContent>
                                <TextField
                                    fullWidth
                                    label="Check-in Comments"
                                    name="comments"
                                    value={checkInForm.comments}
                                    onChange={handleCheckInFormChange}
                                    multiline
                                    rows={3}
                                    placeholder="Any special instructions or notes..."
                                    sx={{ mb: 3 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Performed By"
                                    name="performed_by"
                                    value={checkInForm.performed_by}
                                    onChange={handleCheckInFormChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Alert severity="warning" sx={{ mt: 3 }}>
                                    <Typography variant="body2">
                                        <strong>Important:</strong> Once checked in, the booking status cannot be reversed to active.
                                        Please ensure all details are correct before proceeding.
                                    </Typography>
                                </Alert>
                            </CardContent>
                        </Card>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    px: 3,
                    py: 2,
                    justifyContent: 'space-between'
                }}>
                    <Box>
                        <Button
                            color="error"
                            onClick={handleCloseCheckIn}
                            variant="outlined"
                            sx={{ mr: 1 }}
                        >
                            Cancel
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            variant="outlined"
                        >
                            Back
                        </Button>

                        {activeStep === steps.length - 1 ? (
                            <Button
                                color="success"
                                variant="contained"
                                onClick={handleCheckIn}
                                startIcon={<CheckCircle />}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Confirm Check-in'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                color="primary"
                                variant="contained"
                            >
                                Next
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}><b>Booking Details</b></DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Booking Number:</b></Typography>
                                    <Typography variant="body1">{selectedRecord.booking_no}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Status:</b></Typography>
                                    {selectedRecord.status === 1 ? (
                                        <Chip
                                            label={getStatusById(selectedRecord.status)}
                                            size="small"
                                            color="warning"
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : selectedRecord.status === 2 ? (
                                        <Chip
                                            label="Checked In"
                                            size="small"
                                            color="success"
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : selectedRecord.status === 3 ? (
                                        <Chip
                                            label="Checked Out"
                                            size="small"
                                            color="info"
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : (
                                        <Chip
                                            label={getStatusById(selectedRecord.status)}
                                            size="small"
                                            color="error"
                                            sx={{ mt: 0.5 }}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Booking Type:</b></Typography>
                                    <Chip
                                        label={getBookingTypeLabel(selectedRecord)}
                                        size="small"
                                        color={selectedRecord.booking_type === 1 ? 'default' : 'secondary'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Check In:</b></Typography>
                                    <Typography variant="body1">{formatCreatedAtDate(selectedRecord.check_in)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Check Out:</b></Typography>
                                    <Typography variant="body1">{formatCreatedAtDate(selectedRecord.check_out)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Room Type:</b></Typography>
                                    <Typography variant="body1">{getCategoriesNameById(selectedRecord.room_type, rooms)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Total Guests:</b></Typography>
                                    <Chip
                                        label={getTotalGuests(selectedRecord)}
                                        size="small"
                                        color="info"
                                        icon={<Group />}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Payment Mode:</b></Typography>
                                    <Typography variant="body1">{selectedRecord.payment_mode}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Total Amount:</b></Typography>
                                    <Typography variant="body1" fontWeight="bold" color="primary">
                                        {formatCurrency(selectedRecord.total_amount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Advance Paid:</b></Typography>
                                    <Typography variant="body1" color="success">
                                        {formatCurrency(selectedRecord.advance_amount)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Balance Due:</b></Typography>
                                    <Typography variant="body1" color="warning">
                                        {formatCurrency(selectedRecord.total_amount - selectedRecord.advance_amount)}
                                    </Typography>
                                </Grid>
                                {selectedRecord.checked_in_by && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Checked In By:</b></Typography>
                                        <Typography variant="body1">{selectedRecord.checked_in_by}</Typography>
                                    </Grid>
                                )}
                                {selectedRecord.actual_check_in && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2"><b>Actual Check In:</b></Typography>
                                        <Typography variant="body1">{formatCreatedAtDate(selectedRecord.actual_check_in)}</Typography>
                                    </Grid>
                                )}
                                {selectedRecord.booking_remarks && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2"><b>Remarks:</b></Typography>
                                        <Typography variant="body1">{selectedRecord.booking_remarks}</Typography>
                                    </Grid>
                                )}
                            </Grid>

                            {selectedRecord.booking_type === 1 ? (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="h6" gutterBottom>Customer Details</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="subtitle2" color="text.secondary">Name:</Typography>
                                            <Typography variant="body1">{selectedRecord.customer_name || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="subtitle2" color="text.secondary">Mobile:</Typography>
                                            <Typography variant="body1">{selectedRecord.customer_mobile || 'N/A'}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="subtitle2" color="text.secondary">Email:</Typography>
                                            <Typography variant="body1">{selectedRecord.customer_email || 'N/A'}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="h6" gutterBottom>Organisation Details</Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Organisation Name:</strong> {selectedRecord.organisation_name || 'N/A'}
                                    </Typography>

                                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                                        <strong>Members ({selectedRecord.organisation_members?.length || 0}):</strong>
                                    </Typography>

                                    {selectedRecord.organisation_members && selectedRecord.organisation_members.length > 0 ? (
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{
                                                            backgroundColor: theme.palette.primary.main,
                                                            '& th': {
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.95rem'
                                                            }
                                                        }}>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Phone</TableCell>
                                                        <TableCell>Email</TableCell>
                                                        <TableCell>Room</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedRecord.organisation_members.map((member, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{member.name}</TableCell>
                                                            <TableCell>{member.phone}</TableCell>
                                                            <TableCell>{member.email || '-'}</TableCell>
                                                            <TableCell>{getCategoriesNameById(member.room_type, rooms)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Typography variant="body2">No members listed</Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Early Check-in Confirmation Modal */}
            <Dialog
                open={earlyCheckInModal}
                onClose={handleEarlyCheckInCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: theme.palette.warning.light,
                    color: theme.palette.warning.dark,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Alert severity="warning" sx={{ p: 0, backgroundColor: 'transparent' }} />
                    Early Check-in Request
                </DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ py: 2 }}>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                You are attempting to check in {selectedRecord?.booking_type === 2 ? 'an organisation' : 'a guest'} earlier than the scheduled time.
                            </Typography>
                        </Alert>

                        <Grid container spacing={3}>
                            {/* Schedule Information */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Schedule Information
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CalendarToday color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Scheduled Check-in" 
                                                    secondary={formatCreatedAtDate(selectedRecord?.check_in)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CalendarToday color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Scheduled Check-out" 
                                                    secondary={formatCreatedAtDate(selectedRecord?.check_out)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <AccessTime color="warning" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Current Time" 
                                                    secondary={new Date().toLocaleString()}
                                                />
                                            </ListItem>
                                        </List>

                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                                            <Typography variant="body2" color="warning.dark">
                                                <strong>Note:</strong> This is {Math.ceil((new Date(selectedRecord?.check_in) - new Date()) / (1000 * 60 * 60))} hours earlier than scheduled.
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Customer Information */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            {selectedRecord?.booking_type === 2 ? 'Organisation Details' : 'Customer Details'}
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {selectedRecord?.booking_type === 2 ? <Business color="primary" /> : <Person color="primary" />}
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={selectedRecord?.booking_type === 2 ? "Organisation Name" : "Customer Name"} 
                                                    secondary={getCustomerDisplayName(selectedRecord)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Phone color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Contact Number" 
                                                    secondary={getCustomerMobile(selectedRecord)}
                                                />
                                            </ListItem>
                                            {selectedRecord?.booking_type === 2 && (
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Group color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Total Members" 
                                                        secondary={selectedRecord?.organisation_members?.length || 0}
                                                    />
                                                </ListItem>
                                            )}
                                            <ListItem>
                                                <ListItemIcon>
                                                    <MeetingRoom color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Room Assigned" 
                                                    secondary={getCategoriesNameById(selectedRecord?.room_type, rooms) || 'N/A'}
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Booking Information */}
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Booking Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Booking Number</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {selectedRecord?.booking_no}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                                                <Chip 
                                                    label={
                                                        (selectedRecord?.advance_amount || 0) >= (selectedRecord?.total_amount || 0) 
                                                        ? "Paid" 
                                                        : "Partial"
                                                    }
                                                    color={
                                                        (selectedRecord?.advance_amount || 0) >= (selectedRecord?.total_amount || 0) 
                                                        ? "success" 
                                                        : "warning"
                                                    }
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Advance Paid</Typography>
                                                <Typography variant="body1" color="success.main">
                                                    {formatCurrency(selectedRecord?.advance_amount || 0)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Balance Due</Typography>
                                                <Typography variant="body1" color="warning.main">
                                                    {formatCurrency((selectedRecord?.total_amount || 0) - (selectedRecord?.advance_amount || 0))}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                                            <Typography variant="body2">
                                                <strong>Check-in Policy:</strong> Early check-ins are subject to room availability. 
                                                Additional charges may apply for check-ins before the standard time.
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Additional Options */}
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Additional Options
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Early Check-in Reason (Optional)"
                                            multiline
                                            rows={2}
                                            placeholder="Reason for early check-in (e.g., flight arrived early, special request...)"
                                            sx={{ mb: 2 }}
                                            onChange={(e) => {
                                                setCheckInForm(prev => ({
                                                    ...prev,
                                                    comments: `Early check-in: ${e.target.value}. ${prev.comments || ''}`
                                                }));
                                            }}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    color="warning"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setCheckInForm(prev => ({
                                                                ...prev,
                                                                comments: `[Early check-in charge may apply] ${prev.comments || ''}`
                                                            }));
                                                        }
                                                    }}
                                                />
                                            }
                                            label="Apply early check-in charges if applicable"
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleEarlyCheckInCancel}
                        color="error"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                    >
                        Cancel Check-in
                    </Button>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            onClick={handleEarlyCheckInConfirm}
                            color="warning"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Proceed with Early Check-in'}
                        </Button>

                        <Button
                            onClick={() => {
                                showToast("Info", "Please update the booking check-in time first", "info");
                                setEarlyCheckInModal(false);
                            }}
                            color="primary"
                            variant="outlined"
                            startIcon={<CloseIcon />}
                        >
                            Reschedule Instead
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CheckIn;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});